import { Component, Input } from '@angular/core';
import { RmsModule } from '../../custom-modules/rms-module/rms.module';

// =====================================================================
// PurchaseProposalComponent — Scope 6.4 customer Purchase Proposal (PDF)
// ---------------------------------------------------------------------
// Rendered off-screen by ReportService.generatePDF('PurchaseProposal', data)
// (html2pdf → letter, portrait), same as Delivery Order / Payment Receipt.
// Customer-facing copy of Bean Logic: only the groups ticked "Print".
// The caller passes everything already worked out — this component only
// lays it out (no data calls), so the PDF shows exactly what was on screen.
// =====================================================================

export interface PurchaseProposalBar {
  label: string;
  value: number;
  crop: boolean;
}

export interface PurchaseProposalRow {
  Section: string;
  Name: string;
  BurnUsed: number;
  OnReserve: number;
  ReservedSpot: number;
  ReservedForward: number;
  ContractThru: string | null;
  Roastery: number;
  NewCrop: string | null;          // 'YYYY-MM-DD'
  CoverThrough: string | null;     // 'YYYY-MM-DD'
  Needed: number;
  Recommended: number;
  Available: number;
  Short: boolean;
  RecText: string;
  DepletionLabel: string;
  Bars: PurchaseProposalBar[];
}

export interface PurchaseProposalData {
  CompanyID: number;
  CompanyName: string;
  WarehouseName: string;
  BurnRange: number;
  BurnWindowStart: string | null;
  BurnWindowEnd: string | null;
  PreparedDate: string;            // 'YYYY-MM-DD'
  IsTestMode: boolean;
  Rows: PurchaseProposalRow[];
}

@Component({
  selector: 'app-purchase-proposal',
  templateUrl: './purchase-proposal.component.html',
  styleUrls: ['./purchase-proposal.component.scss'],
  imports: [RmsModule]
})
export class PurchaseProposalComponent {
  @Input() data: PurchaseProposalData | null = null;   // set by ReportService before render

  get Rows(): PurchaseProposalRow[] {
    return this.data?.Rows ?? [];
  }

  /** rows grouped by section, in screen order */
  get Sections(): { name: string; rows: PurchaseProposalRow[] }[] {
    const names: string[] = [];
    for (const r of this.Rows) if (!names.includes(r.Section)) names.push(r.Section);
    return names.map(n => ({ name: n, rows: this.Rows.filter(r => r.Section === n) }));
  }

  /** months in the bar (12 or 24) for the column title */
  get BarCount(): number {
    return this.Rows.length ? this.Rows[0].Bars.length : 12;
  }

  get TotalRecommended(): number {
    return this.Rows.reduce((t, r) => t + (r.Recommended || 0), 0);
  }

  get TotalOnReserve(): number {
    return this.Rows.reduce((t, r) => t + (r.OnReserve || 0), 0);
  }

  get ToBook(): PurchaseProposalRow[] {
    return this.Rows.filter(r => r.Recommended > 0);
  }

  barClass(b: PurchaseProposalBar, burn: number): string {
    if (b.value < 0) return 'neg';
    if (burn > 0 && b.value < burn) return 'low';
    return 'ok';
  }
}
